import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const emptyLine = { item_id: '', transfer_qty: '', remarks: '' };

function FoodStockTransfer({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ from_store_id: '', to_store_id: '', lines: [{ ...emptyLine }] });

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = (extra = {}) => ({ Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId, ...extra });
  const parseResponse = async (res) => {
    const raw = await res.text();
    try { const json = raw ? JSON.parse(raw) : {}; return { data: json, message: json?.error || '' }; } catch { return { data: {}, message: raw || '' }; }
  };

  const loadBase = async () => {
    if (!chainId || !propertyId) return;
    try {
      const [sRes, iRes, tRes] = await Promise.all([
        fetch('/api/v1/food/masters/stores', { headers: headers() }),
        fetch('/api/v1/food/masters/items', { headers: headers() }),
        fetch('/api/v1/food/transfers', { headers: headers() })
      ]);
      const s = await parseResponse(sRes); const i = await parseResponse(iRes); const t = await parseResponse(tRes);
      if (!sRes.ok) return setError(s.message || 'Failed to load stores');
      if (!iRes.ok) return setError(i.message || 'Failed to load items');
      if (!tRes.ok) return setError(t.message || 'Failed to load transfers');
      setStores(Array.isArray(s.data) ? s.data : []);
      setItems(Array.isArray(i.data) ? i.data : []);
      setRows(Array.isArray(t.data) ? t.data : []);
    } catch { setError('Network error while loading transfer module'); }
  };

  useEffect(() => {
    if (!chainId || !propertyId) return setError('Select a property first from Hotel Config');
    setError(''); setMessage(''); loadBase();
  }, [token, chainId, propertyId]);

  const setLine = (idx, patch) => setForm((p) => ({ ...p, lines: p.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const addLine = () => setForm((p) => ({ ...p, lines: [...p.lines, { ...emptyLine }] }));
  const removeLine = (idx) => setForm((p) => ({ ...p, lines: p.lines.filter((_, i) => i !== idx) }));

  const createTransfer = async () => {
    setError(''); setMessage('');
    if (!form.from_store_id || !form.to_store_id) return setError('Select from and to stores');
    const lines = form.lines.map((l) => ({ item_id: l.item_id, transfer_qty: Number(l.transfer_qty || 0), remarks: l.remarks || null })).filter((l) => l.item_id && l.transfer_qty > 0);
    if (!lines.length) return setError('Add at least one valid line');
    try {
      const res = await fetch('/api/v1/food/transfers', {
        method: 'POST', headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ from_store_id: form.from_store_id, to_store_id: form.to_store_id, lines })
      });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Create transfer failed');
      setMessage(`Transfer created: #${data.transfer_no}`);
      setForm({ from_store_id: '', to_store_id: '', lines: [{ ...emptyLine }] });
      loadBase();
    } catch { setError('Network error while creating transfer'); }
  };

  const openDetail = async (id) => {
    setError('');
    try {
      const res = await fetch(`/api/v1/food/transfers/${id}`, { headers: headers() });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Failed to load transfer');
      setDetail(data);
    } catch { setError('Network error while loading transfer detail'); }
  };

  const approve = async (id) => {
    setError(''); setMessage('');
    try {
      const res = await fetch(`/api/v1/food/transfers/${id}/approve`, { method: 'POST', headers: headers() });
      const { message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Approve failed');
      setMessage('Transfer approved');
      setDetail(null);
      loadBase();
    } catch { setError('Network error while approving transfer'); }
  };

  const inputStyle = { padding: '0.5rem', border: '1px solid #dcdde1', borderRadius: '6px' };
  const btnBase = { padding: '0.5rem 0.85rem', border: 'none', borderRadius: '6px', cursor: 'pointer' };

  return (
    <div className="page-shell">
      <h2 style={{ color: '#2f855a' }}>Food Management - Stock Transfer</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}
      <div className="filter-bar">
        <h3>Create Transfer</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
          <select value={form.from_store_id} onChange={(e) => setForm({ ...form, from_store_id: e.target.value })} style={inputStyle}>
            <option value="">From Store</option>{stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
          </select>
          <select value={form.to_store_id} onChange={(e) => setForm({ ...form, to_store_id: e.target.value })} style={inputStyle}>
            <option value="">To Store</option>{stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
          </select>
        </div>
        {form.lines.map((l, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '2fr 1fr 2fr auto', gap: '0.5rem', marginBottom: '0.4rem' }}>
            <select value={l.item_id} onChange={(e) => setLine(idx, { item_id: e.target.value })} style={inputStyle}>
              <option value="">Select Item</option>{items.map((i) => <option key={i.id} value={i.id}>{`${i.item_code} - ${i.item_name}`}</option>)}
            </select>
            <input type="number" min="0.001" step="0.001" value={l.transfer_qty} onChange={(e) => setLine(idx, { transfer_qty: e.target.value })} style={inputStyle} placeholder="Qty" />
            <input value={l.remarks} onChange={(e) => setLine(idx, { remarks: e.target.value })} style={inputStyle} placeholder="Remarks" />
            <button onClick={() => removeLine(idx)} className="btn-danger" style={btnBase}>Remove</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={addLine} className="btn-primary" style={btnBase}>Add Line</button>
          <button onClick={createTransfer} className="btn-success" style={btnBase}>Create Transfer</button>
        </div>
      </div>

      <div className="filter-bar">
        <h3>Transfer History</h3>
        {rows.length === 0 ? <p>No transfers found</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead><tr style={{ background: '#2f855a', color: '#fff' }}>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>No</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>From</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>To</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Qty</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Status</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Action</th>
            </tr></thead>
            <tbody>{rows.map((r) => <tr key={r.id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
              <td style={{ padding: '0.5rem' }}>{r.transfer_no}</td><td style={{ padding: '0.5rem' }}>{r.from_store_name}</td><td style={{ padding: '0.5rem' }}>{r.to_store_name}</td><td style={{ padding: '0.5rem' }}>{Number(r.transfer_qty || 0).toFixed(3)}</td><td style={{ padding: '0.5rem' }}>{r.status}</td><td style={{ padding: '0.5rem' }}><button onClick={() => openDetail(r.id)} className="btn-primary" style={btnBase}>View</button></td>
            </tr>)}</tbody>
          </table>
        )}
      </div>

      {detail && (
        <div className="filter-bar">
          <h3>{`Transfer #${detail.transfer_no}`}</h3>
          <p>{`From ${detail.from_store_name} to ${detail.to_store_name} | Status: ${detail.status}`}</p>
          {(detail.items || []).map((i) => <div key={i.id}>{`${i.item_code} - ${i.item_name} : ${Number(i.transfer_qty || 0).toFixed(3)}`}</div>)}
          <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.5rem' }}>
            {detail.status === 'PENDING' && <button onClick={() => approve(detail.id)} className="btn-success" style={btnBase}>Approve Transfer</button>}
            <button onClick={() => setDetail(null)} className="btn-muted" style={btnBase}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FoodStockTransfer;
